import { EventForm } from '@components/eventCreation/CreateEventUtils';
import { EventContextValues } from '@hooks/useEventContext';
import { EventWithMembership } from '@modules/supabase/events';
import {
    Class,
    Division,
    DIVISION_LIMITATION,
    Event,
    EVENT_ROLE,
    EventRoleStatus,
    WeighInItem,
} from '@type/db-types';

type PartialEvent = Partial<
    Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'divisions' | 'weighInAt'>
> &
    Partial<
        Pick<Event, 'id' | 'createdAt' | 'updatedAt'> & {
            weighInAt: WeighInItem[] | null; // technically it can be any JSON, but we want to enforce this type when possible
        }
    >;

// Removes the 'divisions' and other fields from the EventForm type
export const mapEventFormToEvent = (form: Partial<EventForm>): PartialEvent => {
    return {
        birthdayCutoff: form.birthdayCutoff,
        createdAt: form.createdAt,
        createdByUserId: form.createdByUserId,
        description: form.description,
        fee: form.fee,
        id: form.id,
        imageUrl: form.imageUrl,
        isPrivate: form.isPrivate,
        location: form.location,
        maxEntries: form.maxEntries,
        name: form.name,
        payToUserId: form.payToUserId,
        placeId: form.placeId,
        startAt: form.startAt,
        status: form.status,
        updatedAt: form.updatedAt,
        waiver: form.waiver,
        weighInAt: form.weighInAt,
        weightTolerance: form.weightTolerance,
    };
};

export const EVENT_ROLE_LOOKUP = {
    [EVENT_ROLE.OWNER]: 'Admin',
    [EVENT_ROLE.PARTICIPANT]: 'Participant',
    [EVENT_ROLE.STAFF]: 'Staff',
    [EVENT_ROLE.VIEWER]: 'Viewer',
};

export const EVENT_ROLE_OPTIONS: { label: string; value: EVENT_ROLE }[] = [
    { value: EVENT_ROLE.OWNER, label: EVENT_ROLE_LOOKUP[EVENT_ROLE.OWNER] },
    { value: EVENT_ROLE.STAFF, label: EVENT_ROLE_LOOKUP[EVENT_ROLE.STAFF] },
    { value: EVENT_ROLE.PARTICIPANT, label: EVENT_ROLE_LOOKUP[EVENT_ROLE.PARTICIPANT] },
    { value: EVENT_ROLE.VIEWER, label: EVENT_ROLE_LOOKUP[EVENT_ROLE.VIEWER] },
];
export function eventFromRow(
    row: EventRoleStatus | EventRoleStatus[],
): EventContextValues['event'] {
    const eventRole: EventRoleStatus = Array.isArray(row) ? row[0] : row;

    const event: EventContextValues['event'] = {
        memberships: [], // will be populated by the EventContextProvider
        ...eventRole,
        waiver: '',
        divisions: [],
    };
    return event;
}

/**
 * Examples:
 * Input: { name: 'Division 1', entryLimitation: 'NONE', minAge: 0, maxAge: 0 } -> Output: 'Division 1'
 * Input: { name: 'Division 2', entryLimitation: 'age', minAge: 10, maxAge: 12 } -> Output: 'Division 2 (ages: 10 - 12)'
 * Input: { name: 'Division 3', entryLimitation: 'grade', minAge: 9, maxAge: 12 } -> Output: 'Division 3 (grades: 9 - 12)'
 */
export const getDivisionLabel = (division: Division | undefined): string => {
    if (division) {
        const minText = `${division.minAge ? (division.maxAge ? `${division.minAge} - ` : '+') : ''}`;
        const maxText = division.maxAge
            ? `${division.minAge ? '' : 'up to '}${division.maxAge}`
            : '';
        const hideLimits =
            division.entryLimitation === DIVISION_LIMITATION.NONE ||
            (!division.minAge && !division.maxAge);
        const limits = hideLimits ? '' : `(${division.entryLimitation}s: ${minText}${maxText})`;
        return `${division.name} ${limits}`;
    }
    return '';
};

export const getClassLabel = (
    localClass: Class | undefined,
    classId?: string | null,
    event?: EventWithMembership | null,
): string => {
    const cls =
        localClass ||
        (classId && event?.divisions.flatMap((d) => d.classes).find((c) => c.id === classId)) ||
        undefined;
    return cls ? `${cls.name} ${cls.maxWeight?.length ? `(max: ${cls.maxWeight})` : ''}` : '';
};
